import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { ProfileBlueprint, ProfileDefaults, ProfilePluginRef, SettingsProfile } from "@/types";

export type ProfileLayer = "managed" | "owner" | "mcpServers";

export interface CreateSettingsProfilePayload {
  name: string;
  description: string;
  config?: {
    managed?: Record<string, unknown>;
    owner?: Record<string, unknown>;
    mcpServers?: Record<string, unknown>;
  };
  // #215 (E2): Gitea-Seed-Repo + Branch (aus den Browse-Dropdowns). null = kein Seed.
  seedRepo?: string | null;
  seedBranch?: string | null;
  // #281: Blaupause (partiell).
  blueprint?: Partial<ProfileBlueprint>;
}

export interface UpdateSettingsProfilePayload {
  name?: string;
  description?: string;
  // #215 (E2): key vorhanden → ändern (null = Seed entfernen); absent → unverändert.
  seedRepo?: string | null;
  seedBranch?: string | null;
  // #281: Blaupausen-Achsen — dieselbe Patch-Semantik (null/[] = bewusst leeren).
  brainImageId?: string | null;
  plugins?: ProfilePluginRef[];
  connectors?: { connectorId: string }[];
  defaults?: ProfileDefaults;
}

function useSettingsProfilesClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

function useInvalidateSettingsProfiles() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.resource("settingsProfiles") });
}

export function useCreateSettingsProfile(): UseMutationResult<
  SettingsProfile,
  Error,
  CreateSettingsProfilePayload
> {
  const getClient = useSettingsProfilesClient();
  const invalidate = useInvalidateSettingsProfiles();
  return useMutation({
    mutationFn: (input) => getClient().post<SettingsProfile>("/settings-profiles", input),
    onSuccess: invalidate,
  });
}

export function useUpdateSettingsProfile(): UseMutationResult<
  SettingsProfile,
  Error,
  { id: string } & UpdateSettingsProfilePayload
> {
  const getClient = useSettingsProfilesClient();
  const invalidate = useInvalidateSettingsProfiles();
  return useMutation({
    mutationFn: ({ id, ...patch }) =>
      getClient().patch<SettingsProfile>(`/settings-profiles/${id}`, patch),
    onSuccess: invalidate,
  });
}

export function useMutateProfileLayer(): UseMutationResult<
  SettingsProfile,
  Error,
  { id: string; layer: ProfileLayer; payload: Record<string, unknown>; mode: "merge" | "replace" }
> {
  const getClient = useSettingsProfilesClient();
  const invalidate = useInvalidateSettingsProfiles();
  return useMutation({
    mutationFn: ({ id, layer, payload, mode }) =>
      getClient().patch<SettingsProfile>(`/settings-profiles/${id}/config/${layer}`, { payload, mode }),
    onSuccess: invalidate,
  });
}

export function useDeleteSettingsProfile(): UseMutationResult<void, Error, string> {
  const getClient = useSettingsProfilesClient();
  const invalidate = useInvalidateSettingsProfiles();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/settings-profiles/${id}`),
    onSuccess: invalidate,
  });
}
